import React, { useMemo, useRef, useState } from 'react';
import AdminSearchTable from '../Admin/AdminSearchTable';
import FilterIcon from '../../SVG/Filter';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import useClickOutside from '../Shared/useClickOutside';
import { useNavigate } from 'react-router-dom';

const ClaimStatusSection = () => {
    const [polNo, setPolNo] = useState('');
    const [provTaxID, setProvTaxID] = useState('');
    const [claimIDNum, setClaimIDNum] = useState('');
    const [allClaims, setAllClaims] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    //env variables
    const apiURL = import.meta.env.VITE_API;

    // Date range picker
    const rangePickerRef = useRef(null);
    const [showRangePicker, setShowRangePicker] = useState(false);
    const [dateFilterActive, setDateFilterActive] = useState(false);
    const [dateRange, setDateRange] = useState([
        { startDate: new Date(), endDate: new Date(), key: 'selection' },
    ]);

    useClickOutside(rangePickerRef, () => setShowRangePicker(false));

    // -----------------------------
    // TABLE COLUMNS
    // -----------------------------
    const columns = [
        { label: 'Claim ID', accessor: 'claimIDNum' },
        { label: 'Bill Date', accessor: 'billDate' },
        { label: 'Service Dates', accessor: 'serviceDatesDisplay' },
        { label: 'Process Date', accessor: 'processDate' },
        { label: 'Billed Amount', accessor: 'billedAmount' },
        { label: 'Amount Covered', accessor: 'amountCovered' },
        { label: 'Check To', accessor: 'checkTo' },
        { label: 'Description', accessor: 'description' },
        {
            label: 'Download EOB',
            accessor: 'download',
            sortable: false,
            Cell: (row) => (
                <span
                    className="paginated-table-row-clickable"
                    onClick={() => downloadEOB(row)}
                >
                    Download
                </span>
            )
        },
    ];

    const downloadEOB = async (claim) => {
        try {
            const payload = {
                PolNo: claim.polNo.substring(0, 10),
                ProvTaxID: claim.providerID.substring(0, 9),
                ClaimIDNum: claim.claimIDNum,
                StartDate: claim.serviceDatesMin
                    ? new Date(claim.serviceDatesMin).toISOString()
                    : null,

                EndDate: claim.serviceDatesMax
                    ? new Date(claim.serviceDatesMax).toISOString()
                    : null,

            };

            const res = await fetch(`${apiURL}/Provider/getEOB`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                navigate('/login');
                return;
            }

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `EOB_${claim.claimIDNum}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();

            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading EOB:', err);
            setError('Failed to download EOB.');
        }
    };

    // -----------------------------
    // HELPERS
    // -----------------------------
    const formatDateLabel = (date) =>
        date
            ? date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            })
            : '';

    const formatRangeLabel = (active, range) => {
        if (!active) return 'All Dates';
        const { startDate, endDate } = range[0] || {};
        if (!startDate || !endDate) return 'All Dates';
        return `${formatDateLabel(startDate)} - ${formatDateLabel(endDate)}`;
    };

    const toIsoDate = (date) =>
        date
            ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
            : null;

    // -----------------------------
    // CLEAR
    // -----------------------------
    const handleClear = () => {
        setPolNo('');
        setProvTaxID('');
        setClaimIDNum('');
        setAllClaims([]);
        setError(null);
        setSearched(false);
        setDateFilterActive(false);
        setShowRangePicker(false);
    };

    // -----------------------------
    // SEARCH
    // -----------------------------
    const handleSearch = async () => {
        setLoading(true);
        setError(null);
        setSearched(true);

        try {
            const start = dateFilterActive ? dateRange[0]?.startDate : null;
            const end = dateFilterActive ? dateRange[0]?.endDate : null;

            const payload = {
                PolNo: polNo || null,
                ProvTaxID: provTaxID || null,
                StartDate: start ? toIsoDate(start) : null,
                EndDate: end ? toIsoDate(end) : null,
                ClaimIDNum: claimIDNum || null,
            };

            const res = await fetch(`${apiURL}/Provider/getClaimStatusLookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (res.status === 401) {
                navigate('/login');
                return;
            }

            if (res.status === 403) {
                navigate('/home');
                return;
            }

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }

            const data = await res.json();
            setAllClaims(data || []);
        } catch (err) {
            console.error('Error fetching claims:', err);
            setError('Failed to load claims.');
            setAllClaims([]);
        } finally {
            setLoading(false);
        }
    };

    const tableData = useMemo(() => allClaims, [allClaims]);

    // -----------------------------
    // RENDER
    // -----------------------------
    return (
        <div className="container container-section">
            <div className="filter-box">
                <FilterIcon />

                <div className="filter-item">
                    <label className="filterLabel">Policy Number:</label>
                    <input
                        className="filterInput"
                        value={polNo}
                        onChange={e => setPolNo(e.target.value)}
                        maxLength={10}
                    />
                </div>

                <div className="filter-item">
                    <label className="filterLabel">Provider Tax ID:</label>
                    <input
                        className="filterInput"
                        value={provTaxID}
                        onChange={e => setProvTaxID(e.target.value)}
                        maxLength={9}
                    />
                </div>

                <div className="filter-item filter-item-range">
                    <label className="filterLabel">Service Date Range:</label>
                    <input
                        readOnly
                        className="filterInput"
                        value={formatRangeLabel(dateFilterActive, dateRange)}
                        onClick={() => setShowRangePicker(prev => !prev)}
                    />

                    {showRangePicker && (
                        <div ref={rangePickerRef} className="date-range-popover themed-popover">
                            <DateRange
                                ranges={dateRange}
                                onChange={item => {
                                    setDateRange([item.selection]);
                                    setDateFilterActive(true);
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="filter-item">
                    <label className="filterLabel">Claim ID Number:</label>
                    <input
                        className="filterInput"
                        value={claimIDNum}
                        onChange={e => setClaimIDNum(e.target.value)}
                    />
                </div>

                <div className="filter-button-box">
                    <button className="filterButton" onClick={handleClear} disabled={loading}>
                        Clear
                    </button>
                    <button className="filterButton" onClick={handleSearch} disabled={loading}>
                        {loading ? 'Searching...' : 'Search'}
                    </button>
                </div>
            </div>

            {error && <div className="claim-status__error">{error}</div>}

            {!loading && searched && tableData.length === 0 && (
                <div className="claim-status__card">
                    <h4>No Claims Found</h4>
                </div>
            )}

            {!loading && tableData.length > 0 && (
                <div className="claim-status__card">
                    <h3 className="claim-status__title">Claim Status Results</h3>
                    <AdminSearchTable columns={columns} data={tableData} />
                </div>
            )}
        </div>
    );
};

export default ClaimStatusSection;
